/**
 * Forgot password page (local auth mode only).
 */

import React, { useState, FormEvent } from 'react';
import Header from '../components/common/Header';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${apiBase}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || 'Request failed');
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ui-background-primary text-ui-text-primary flex flex-col">
      <Header type="homepage" />

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        {submitted ? (
          <div className="w-full max-w-sm text-center space-y-4">
            <h2 className="text-2xl font-semibold">Check your email</h2>
            <p className="text-sm text-ui-text-tertiary">
              If an account with that email exists, we've sent a password reset link.
            </p>
            <a href="/" className="text-lime-500 hover:underline text-sm">
              Back to sign in
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-5">
            <h2 className="text-2xl font-semibold text-center">Forgot password</h2>

            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            <div className="space-y-1">
              <label htmlFor="fp-email" className="block text-sm font-medium text-ui-text-secondary">
                Email
              </label>
              <input
                id="fp-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-ui-border-primary bg-ui-background-secondary px-3 py-2 text-sm text-ui-text-primary placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-lime-400"
                placeholder="you@company.com"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-md bg-lime-500 hover:bg-lime-600 disabled:opacity-50 text-white font-medium py-2 text-sm transition-colors"
            >
              {isSubmitting ? 'Sending…' : 'Send reset link'}
            </button>

            <p className="text-center text-sm text-ui-text-tertiary">
              <a href="/" className="text-lime-500 hover:underline">Back to sign in</a>
            </p>
          </form>
        )}
      </main>
    </div>
  );
};

export default ForgotPasswordPage;
